import React, { useCallback, useMemo, useRef, useState } from 'react'

import { BoxModalContextValue, BoxModalStatus, OneClickSuccessData } from '../../types'
import { BoxLockContext } from '../context'

type ModalProviderProps = {
  children: React.ReactNode
}

export function ModalProvider(props: ModalProviderProps) {
  const { children } = props

  const [status, setStatus] = useState<BoxModalStatus>('closed')
  const [prefetch, setPrefetch] = useState<boolean>(false)
  const [oneClickSuccessData, setOneClickSuccessData] = useState<OneClickSuccessData | null>(null)
  const prevStatusRef = useRef<BoxModalStatus | null>(null)
  const [enableSuccessScreen, setEnableSuccessScreen] = useState<boolean>(false)

  const setStatusWrapper = useCallback((newStatus: BoxModalStatus) => {
    setStatus((prevStatus) => {
      prevStatusRef.current = prevStatus
      return newStatus
    })
  }, [])

  const setOpen = useCallback(
    (open: boolean) => {
      if (open) {
        setStatusWrapper('open')
      } else {
        setStatusWrapper('closed')
      }
    },
    [setStatusWrapper]
  )

  const enablePrefetch = useCallback(() => {
    setPrefetch(true)
  }, [])

  const isSuccessModalOpen = useMemo(
    () => enableSuccessScreen && status === 'success' && !!oneClickSuccessData,
    [enableSuccessScreen, status, oneClickSuccessData]
  )

  // This sets the shared status to 'closed', which closes both modals
  // and clears the success data
  const closeSuccessModal = useCallback(() => {
    setStatusWrapper('closed')
    setOneClickSuccessData(null)
  }, [setStatusWrapper])

  const providerValue: BoxModalContextValue = useMemo(
    () => ({
      componentType: 'modal',
      initialized: true,
      status,
      setStatus: setStatusWrapper,
      prevStatus: prevStatusRef.current,
      open: !['closed', 'success'].includes(status),
      interactive: ['ready', 'error'].includes(status),
      setOpen,
      prefetch,
      enablePrefetch,
      oneClickSuccessData,
      setOneClickSuccessData,
      enableSuccessScreen,
      setEnableSuccessScreen,
      isSuccessModalOpen,
      closeSuccessModal,
    }),
    [
      status,
      setStatusWrapper,
      setOpen,
      prefetch,
      enablePrefetch,
      oneClickSuccessData,
      enableSuccessScreen,
      setEnableSuccessScreen,
      isSuccessModalOpen,
      closeSuccessModal,
    ]
  )

  return <BoxLockContext.Provider value={providerValue}>{children}</BoxLockContext.Provider>
}
